import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { FiBell } from "react-icons/fi";
import NotificationPopup from "./NotificationPopup";
import {
  fetchActiveNotifications,
  getUserNotificationStatus,
  updateLastSeen,
  getUnreadCount,
} from "../services/userNotificationService";

const NotificationBell = ({ collapsed = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [readIds, setReadIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [popupStyle, setPopupStyle] = useState({});
  const bellRef = useRef(null);
  const popupRef = useRef(null);
  const userId = localStorage.getItem("userId");

  const unreadCount = getUnreadCount(notifications, readIds);

  const loadData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [notifs, status] = await Promise.all([
        fetchActiveNotifications(20),
        getUserNotificationStatus(userId),
      ]);
      setNotifications(notifs);
      setReadIds(status.readNotificationIds || []);
    } catch (err) {
      console.error("NotificationBell loadData error:", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadData();
    // Refresh every 5 minutes
    const interval = setInterval(loadData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Close popup on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      // If clicking inside the bell button, let handleToggle handle it
      if (bellRef.current && bellRef.current.contains(e.target)) {
        return;
      }
      // If clicking outside the popup, close it
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleToggle = async () => {
    const willOpen = !isOpen;
    
    if (willOpen && bellRef.current) {
      const rect = bellRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 768;
      
      if (isMobile) {
        setPopupStyle({
          top: `${rect.bottom + 10}px`,
          right: '16px',
          width: 'calc(100vw - 32px)',
          maxWidth: '380px'
        });
      } else {
        // If the bell is too far right, align right instead of left
        const spaceOnRight = window.innerWidth - rect.left;
        if (spaceOnRight < 400) {
          setPopupStyle({
            top: `${rect.bottom + 10}px`,
            right: `${window.innerWidth - rect.right}px`,
            width: '380px'
          });
        } else {
          setPopupStyle({
            top: `${rect.bottom + 10}px`,
            left: `${rect.left}px`,
            width: '380px'
          });
        }
      }
    }

    setIsOpen(willOpen);
    if (willOpen) {
      await updateLastSeen(userId);
      await loadData(); // Re-fetch latest readIds to sync with NotificationScreen
    }
  };

  const handleMarkRead = (notifId) => {
    setReadIds((prev) => [...new Set([...prev, notifId])]);
  };

  return (
    <div ref={bellRef} className="relative">
      <button
        onClick={handleToggle}
        className={`relative flex items-center justify-center transition-all duration-200 rounded-xl hover:bg-slate-700/50 ${
          collapsed ? "w-10 h-10 mx-auto" : "w-10 h-10"
        } ${isOpen ? "bg-slate-700/70" : ""}`}
        title="Thông báo"
      >
        <FiBell
          className={`transition-colors duration-200 ${
            unreadCount > 0 ? "text-amber-400" : "text-slate-300"
          }`}
          size={collapsed ? 18 : 20}
        />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full shadow-lg animate-pulse">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && document.body && createPortal(
        <NotificationPopup
          style={popupStyle}
          popupRef={popupRef}
          notifications={notifications}
          readIds={readIds}
          loading={loading}
          onMarkRead={handleMarkRead}
          onClose={() => setIsOpen(false)}
        />,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;
